import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ScrollArea from '../../../components/atoms/ScrollArea/ScrollArea';
import { ContentList, ContentListItem } from '../ContentList';
import { RefreshCallback } from '../../BlockFetcher';
import { DeletableItemMode, DeletableItem } from './partials/DeletableItem';
import IForm from '../../../types/IForm';
import ContentMessage from '../ContentMessage/ContentMessage';
import { useDelayed } from '../../../hooks/useDelayed';
import noop from '../../../lib/doNothing';

type DeletableItem = {
  id: string;
  name?: string;
  removeForm: IForm;
};

type DeletableItemListProps<T> = {
  children(item: T): React.ReactNode;
  items: T[];
  refresh?: RefreshCallback;
  namespaceAndKey: string;
};

export function DeletableItemList<T extends DeletableItem>({
  namespaceAndKey,
  refresh,
  items,
  children,
}: DeletableItemListProps<T>) {
  const { t } = useTranslation();
  const [activeItem, setActiveItem] = useState<string>();
  const [activeMode, setActiveMode] = useState<DeletableItemMode>();
  const removeItem = useDelayed(refresh || noop);

  /**
   * If PAL is closed, states are preserved. This is not correct,
   * when user changes state outside the PAL on the page itself.
   * See: SON-14107
   */
  useEffect(() => {
    setActiveMode(undefined);
  }, [items.length]);

  if (items?.length === 0) {
    return <ContentMessage text={t(`${namespaceAndKey}.noContent`)} />;
  }

  // some list have websocket connections -> no need for manual refreshing this way.
  // only use if refresh function is given.

  return (
    <ScrollArea to={100} bottomOffset={10}>
      <div className="deletable-item-list">
        <ContentList ordered>
          {items.map((item, index) => {
            return (
              <ContentListItem key={item.id}>
                <DeletableItem
                  id={item.id}
                  name={item.name}
                  namespace={namespaceAndKey}
                  position={index + 1}
                  numberOfItems={items.length}
                  mode={activeItem === item.id ? activeMode : undefined}
                  removeForm={item.removeForm}
                  removeItem={removeItem}
                  toggleMode={(mode: DeletableItemMode) => {
                    setActiveItem(item.id);
                    setActiveMode(mode);
                  }}
                >
                  {children(item)}
                </DeletableItem>
              </ContentListItem>
            );
          })}
        </ContentList>
      </div>
    </ScrollArea>
  );
}
